/* istanbul ignore file */
import * as HighCharts from 'highcharts'
import React from 'react'
import DarkUnica from 'highcharts/themes/dark-unica'
import ReactApexChart, { type Props } from 'react-apexcharts'
import { type kpiDailyState } from '../redux/kpiDailySlice'
import { type RootState } from '../../../store'
import { connect, type ConnectedProps } from 'react-redux'
import { fetchKpiDailyData } from '../redux/actionCreators'
import { useCircleGaugeParams } from '../../../customHooks/circleGaugeDim'
import { Card, CardContent, CircularProgress, Grid, Divider, Typography } from '@mui/material'
import AuthError from '../../../components/ErrorComponents/AuthError'
import deleteIcon from '../../../assets/deleteIcon.svg'
import { type KpiKey } from '../redux/changeKpiType'
import { getKpiInfo } from './KpiComponents/kpiInfo'
import { type KpiLiveDataObjWithCategory } from '../types'
import LanguageStrings from '../../../i18n/locales'
import { parseDateInAOHstandard } from '../../../customHooks/parseDate'
import GenericToggleButton, { type ToggleOption } from '../../../components/GenericToggleButton'
import { type GeneratedData, MultiSelectDropdown } from './MultiSelectDropdown'

const dataCompletenessStrings = LanguageStrings().AMIDataCollectionStrings.dataCompleteness

interface GeneratedKpiData {
  percentage: number[]
  assetType: string[]
  color: string[]
}

const baseColors: string[] = ['#A78D34', '#1271B1', '#4881FE', '#1AD3D3', '#F1693EFD', '#F1B33E']
// produce an array of their values for passing to the series data of the chart
function createSeriesData (kpidataArr: GeneratedData[]): GeneratedKpiData {
  // build tuples and sort by percentage
  const tuples = kpidataArr.map((item, idx) => ({
    percentage: Number(item.percentage.toFixed(2)),
    assetType: item.asset_type
  }))

  tuples.sort((a, b) => b.percentage - a.percentage)

  const percentage = tuples.map(t => t.percentage)
  const assetType = tuples.map(t => t.assetType)
  const color = tuples.map((_, i) => baseColors[i % baseColors.length])

  return { percentage, assetType, color }
}

function getReactApexChart (seriesData: GeneratedKpiData, dailyKpis: kpiDailyState, height: string, centerFont: string, fetchKpis: () => void): any {
  const chartOptions: Props = {
    series: seriesData.percentage,
    options: {
      chart: {
        type: 'radialBar'
      },
      stroke: {
        // This puts a little circle in the chart when it's zero so the
        // chart doesn't look like it's missing data.
        lineCap: 'butt'
      },
      fill: {
        // If there is more series data than colors it repeats
        colors: seriesData.color
      },
      plotOptions: {
        radialBar: {
          hollow: {
            size: '35%'
          },
          inverseOrder: true,
          dataLabels: {
            name: {
              show: false
            },
            value: {
              show: true,
              fontSize: centerFont,
              fontWeight: 400,
              color: 'white',
              offsetY: 8
            },
            total: {
              show: true,
              label: dataCompletenessStrings.total,
              fontSize: centerFont,
              formatter: function (e) {
                // All of this because the default center avgs the series data
                // but produces a string tha doesn't fit on the screen for cases
                // like 66.3333333 etc.
                // The documentation leaves a lot to be desired. I set a breakpoint to
                // capture the event. I'm assuming here that the config is always present.
                const seriesData = e.config.series
                if (seriesData.length === 0) {
                  return 'No Data'
                } else {
                  const avg = ((seriesData.reduce((partialSum: number, a: number) => partialSum + a, 0)) / seriesData.length)
                  return avg.toFixed(2) + '%'
                }
              }
            }
          },
          track: {
            background: '#202020AA',
            strokeWidth: '45px',
            show: true
          }
        }
      }
    }
  }

  if (dailyKpis.isLoading) {
    return (
      <div style={{ textAlign: 'center' }}>
          <CircularProgress />
      </div>
    )
  } else if (dailyKpis.httpStatus === 200 && dailyKpis.dataCompleteness?.liveData !== undefined && dailyKpis.dataCompleteness?.liveData.length > 0) {
    return (
      <>
        <div style={{ width: '45%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <ReactApexChart
              options={chartOptions.options}
              series={chartOptions.series}
              type='radialBar' height={height} width={height}
            />
          </div>
          <div className="ami-chart-legends" style={{ width: '45%', marginTop: '10px' }}>
            {getLegend(seriesData, dailyKpis, dailyKpis.httpStatus)}
          </div>
      </>

    )
  } else if (dailyKpis.httpStatus === 401) {
    return (
      <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="Unauthorized"/></div>
    )
  } else if (dailyKpis.httpStatus === 403) {
    return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage='accessForbidden'/></div>
    )
  } else if (dailyKpis.httpStatus === 502 || dailyKpis.httpStatus === 503) {
    return (
      <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage='cannotFetch' retry={fetchKpis}/></div>
    )
  } else if (dailyKpis.httpStatus === 200 && dailyKpis.dataCompleteness?.liveData !== undefined && dailyKpis.dataCompleteness?.liveData.length === 0) {
    return (
      <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage='NoDataPresent'/></div>
    )
  } else {
    return (
      <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage='cannotFetch' retry={fetchKpis}/></div>
    )
  }
}

function getLegend (seriesData: GeneratedKpiData, dailyKpis: kpiDailyState, httpStatus: any): any {
  const legendRows: JSX.Element[] = []
  for (let i = 0; i < seriesData.percentage.length; i++) {
    legendRows.push(
      <div className="ami-legend-row" key={`${seriesData.assetType[i]}-${i}`}>
        <div className="ami-legend-left">
          <div className="ami-legend-color" style={{ background: seriesData.color[i] }} />
          <div
            className="ami-legend-label"
            title={seriesData.assetType[i]}
          >
            {seriesData.assetType[i]}
          </div>
        </div>
        <div className="ami-legend-value">
          {seriesData.percentage[i]} %
        </div>
      </div>
    )
  }
  if (httpStatus !== 401 && httpStatus !== 403) {
    return legendRows
  } else {
    return []
  }
}

interface ParentProps {
  fetchKpis: () => void
  dataCompRef: React.MutableRefObject<null>
  handleKpiRemove: (kpi: KpiKey) => void
}

function getCategoryOptions (data: KpiLiveDataObjWithCategory[]): ToggleOption[] {
  const categories = data.map(item => item.asset_cat)
  return categories.map(category => ({
    label: category,
    value: category
  }))
}

type CategoryKey = string

const DataCompleteness = (props: AllProps): any => {
  DarkUnica(HighCharts)
  const [height, centerFont] = useCircleGaugeParams()

  const [selectedCategory, setSelectedCategory] = React.useState<ToggleOption>({ label: 'Water', value: 'Water' })
  const [categoryOptions, setCategoryOptions] = React.useState<ToggleOption[]>([])
  const [selectedByCategory, setSelectedByCategory] = React.useState<Record<CategoryKey, GeneratedData[]>>({})
  const [selectedValues, setSelectedValues] = React.useState<GeneratedData[]>([])
  const DEFAULT_SELECTION_COUNT = 6

  const seriesData = React.useMemo(
    () => createSeriesData(selectedValues),
    [selectedValues]
  )

  React.useEffect(() => {
    const opts = getCategoryOptions(props.kpiDaily.dataCompleteness?.liveData ?? [])
    setCategoryOptions(opts)
  }, [props.kpiDaily])

  // Current dropdown options for selected category
  const dropdownOptions = React.useMemo(() => {
    const data = props.kpiDaily.dataCompleteness?.liveData?.find(each => each.asset_cat === selectedCategory.value)
    return (data?.dataPoint ?? [])
  }, [props.kpiDaily, selectedCategory.value])

  React.useEffect(() => {
    const all = dropdownOptions
    if (all.length === 0) {
      setSelectedValues([])
      return
    }

    // Pull previous selection for this category
    const stored = selectedByCategory[selectedCategory.value] ?? []

    // Reconcile with current options (remove items no longer present)
    const allowed = new Set(all.map(o => o.asset_type))
    const reconciled = stored.filter(sv => allowed.has(sv.asset_type))

    if (reconciled.length > 0) {
      setSelectedValues(reconciled)
    } else {
      const take = Math.min(DEFAULT_SELECTION_COUNT, all.length)
      setSelectedValues(all.slice(0, take))
    }
  }, [selectedCategory.value, dropdownOptions, selectedByCategory])

  const handleMultiSelectChange = (vals: GeneratedData[]): void => {
    setSelectedValues(vals)
    setSelectedByCategory(prev => ({
      ...prev,
      [selectedCategory.value]: vals
    }))
  }

  function lastUpdatedTime (): string {
    const timestamp = props.kpiDaily.dataCompleteness?.CalculatedAt !== undefined && props.kpiDaily.dataCompleteness?.CalculatedAt !== null ? new Date(props.kpiDaily.dataCompleteness?.CalculatedAt) : ''
    const dayJsUtcTime = parseDateInAOHstandard(timestamp)

    const lastUpdated = `${dataCompletenessStrings.lastUpdatedStart} ${dayJsUtcTime}`

    return lastUpdated
  }

  return (
    <div style={{ height: window.innerWidth >= 1896 ? 457 : 382 }}>
      <Card ref={props.dataCompRef} sx={{ height: '100%', borderRadius: '10px', backgroundColor: '#313030', color: '#f0f0f0', display: 'flex', flexDirection: 'column' }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div data-testid='data-completeness' style={{ fontSize: window.innerWidth > 1300 ? '16px' : '13px', padding: '15px', paddingBottom: 0, letterSpacing: '1px', textTransform: 'uppercase' }}>DATA COMPLETENESS {getKpiInfo('Data Completeness')}</div>
          <img src={deleteIcon} onClick={() => { props.handleKpiRemove('Data Completeness') }} style={{ marginRight: 15, cursor: 'pointer' }} alt='delete'/>
        </div>
        <div style={{ padding: '0px 1px 15px 15px', fontSize: 14, fontWeight: 300 }}>
          <span>
            {props.kpiDaily.dataCompleteness?.CalculatedAt !== null && props.kpiDaily.dataCompleteness?.CalculatedAt !== undefined && lastUpdatedTime()}
          </span>
        </div>
      </div>
      <CardContent sx={{ flex: 1 }}>
          {props.kpiDaily.dataCompleteness?.liveData !== undefined && props.kpiDaily.dataCompleteness?.liveData.length > 0
            ? <Grid sx={{ marginTop: '-20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '2px' }}>
                  <GenericToggleButton options={categoryOptions} selected={selectedCategory} onChange={(option) => { setSelectedCategory(option) }} />
                  <div style={{ display: 'flex', gap: '5px', fontFamily: 'Honeywell Sans Web', alignItems: 'center' }}>
                    <Typography className='multi-dropdown-label' sx={{ fontSize: '16px', fontWeight: 500 }}>{dataCompletenessStrings.deviceType}</Typography>
                    <MultiSelectDropdown
                      options={dropdownOptions}
                      selectedValues={selectedValues}
                      onChange={handleMultiSelectChange}
                      placeholder="Select an option"
                      defaultSelectCount={DEFAULT_SELECTION_COUNT}
                      minSelection={1}
                      maxSelection={6}
                    />
                  </div>
              </div>
              <Divider
                orientation="horizontal"
                sx={{
                  marginTop: '5px',
                  backgroundColor: '#272727',
                  height: '1px',
                  width: 'auto'
                }}
              />
              </Grid>
            : null
          }
        <Grid>
          <div id="data-comp-container" className="centerChart">
            {getReactApexChart(seriesData, props.kpiDaily, height, centerFont, props.fetchKpis)}
          </div>
        </Grid>
      </CardContent>
    </Card>
    </div>
  )
}

interface DispatchToProps {
  fetchKpiDailyData: (tenant: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchKpiDailyData: () => dispatch(fetchKpiDailyData())
})

interface StateToProps {
  kpiDaily: kpiDailyState
  activeTenant: string
}

const mapStateToProps = (state: RootState): StateToProps => ({
  kpiDaily: state.kpiDailyData,
  activeTenant: state.tenant
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
type AllProps = PropsFromRedux & ParentProps
export default connector(DataCompleteness)
